import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rss } from "lucide-react";
import { api, API } from "../lib/api";
import { VoiceAudioPlayer } from "../components/VoiceAudioPlayer";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function hasAudio(t) {
  return !!(t.audio_url || (t.audios && Object.values(t.audios).some(Boolean)));
}

export default function Audios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audios");
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div
      className="max-w-5xl mx-auto px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-32"
      data-testid="audios-page"
    >
      <div
        className="text-xs uppercase tracking-[0.35em] mb-6"
        style={{ color: "var(--rincon-secondary)" }}
      >
        Anaquel de voz
      </div>
      <h1
        className="font-display text-5xl md:text-7xl tracking-tight"
        style={{ color: "var(--rincon-text)" }}
      >
        Poemas <em className="italic">con voz</em>.
      </h1>
      <p
        className="font-body italic mt-6 max-w-2xl text-xl leading-relaxed"
        style={{ color: "var(--rincon-text-muted)" }}
      >
        Textos recitados por el propio autor. Escúchalos aquí, o entra en{" "}
        <em>El Poemario</em> para una lectura inmersiva con música.
      </p>

      <div className="mt-10 flex flex-wrap items-center gap-6">
        <a
          href="/poemario/index.html"
          data-testid="audios-poemario-link"
          className="inline-flex items-center gap-2 px-6 py-3 font-display text-lg transition-colors"
          style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
        >
          ❦ Entrar en El Poemario, con música →
        </a>
        <a
          href={`${API}/feed.xml`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="audios-rss-link"
          className="inline-flex items-center gap-2 font-display text-lg ink-link transition-colors"
          style={{ color: "var(--rincon-secondary)" }}
        >
          <Rss className="w-4 h-4" /> Suscríbete por RSS
        </a>
      </div>

      {loading ? (
        <div
          className="py-24 text-center font-display italic text-2xl"
          style={{ color: "var(--rincon-text-muted)" }}
          data-testid="audios-loading"
        >
          Cargando recitales...
        </div>
      ) : items.length === 0 ? (
        <div
          className="py-24 text-center font-display italic text-2xl"
          style={{ color: "var(--rincon-text-muted)" }}
          data-testid="audios-empty"
        >
          Aún no hay recitales disponibles. Vuelve pronto.
        </div>
      ) : (
        <>
          <div
            className="mt-16 mb-10 pb-4 border-b flex items-end justify-between"
            style={{ borderColor: "var(--rincon-border)" }}
          >
            <div
              className="text-xs uppercase tracking-[0.3em] font-semibold"
              style={{ color: "var(--rincon-primary)" }}
            >
              El recital
            </div>
            <div className="font-display text-base" style={{ color: "var(--rincon-text)" }}>
              <span
                className="font-bold"
                style={{ color: "var(--rincon-primary)" }}
                data-testid="audios-count"
              >
                {items.length}
              </span>{" "}
              <span style={{ color: "var(--rincon-text-muted)" }}>
                {items.length === 1 ? "poema con voz" : "poemas con voz"}
              </span>
            </div>
          </div>

          <ul className="space-y-10" data-testid="audios-list">
            {items.map((t) => (
              <li
                key={t.id}
                className="border-l-2 pl-6 py-1"
                style={{ borderColor: "var(--type-poema, var(--rincon-secondary))" }}
                data-testid={`audio-item-${t.id}`}
              >
                <div
                  className="text-[10px] uppercase tracking-[0.25em] mb-2 font-semibold"
                  style={{ color: "var(--rincon-secondary)" }}
                >
                  {t.subcategory} · {formatDate(t.created_at)}
                </div>
                <Link
                  to={`/texto/${t.id}`}
                  className="font-display text-2xl md:text-3xl tracking-tight transition-colors hover:text-[var(--rincon-primary)]"
                  style={{ color: "var(--rincon-text)" }}
                  data-testid={`audio-title-${t.id}`}
                >
                  {t.title}
                </Link>
                {t.epigraph ? (
                  <p
                    className="mt-2 font-body italic text-base"
                    style={{ color: "var(--rincon-secondary)" }}
                  >
                    «{t.epigraph}»
                  </p>
                ) : t.first_verse ? (
                  <p
                    className="mt-2 font-body italic text-base"
                    style={{ color: "var(--rincon-text-muted)" }}
                  >
                    {t.first_verse}
                  </p>
                ) : null}

                {hasAudio(t) ? (
                  <div className="mt-4">
                    <VoiceAudioPlayer
                      audios={t.audios}
                      audioUrl={t.audio_url}
                      idBase={t.id}
                    />
                  </div>
                ) : null}

                <a
                  href={`/poemario/index.html#poema=${t.id}`}
                  data-testid={`audio-poemario-${t.id}`}
                  className="inline-flex items-center gap-2 mt-3 text-sm font-body italic transition-colors hover:opacity-70"
                  style={{ color: "var(--rincon-secondary)" }}
                >
                  ❦ Escuchar en El Poemario, con música →
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
